import type { ImportJobStatus } from '../services/importJobApi';

export const FINAL_IMPORT_STATUSES: ImportJobStatus[] = ['READY', 'FAILED', 'CANCELLED'];

export const isFinalImportStatus = (status: ImportJobStatus): boolean =>
    FINAL_IMPORT_STATUSES.includes(status);

/** Same threshold as the server's re-enqueue cron. */
export const IMPORT_STUCK_AFTER_MS = 2 * 60_000;

export const IMPORT_STEPS = ['Preparing', 'Saving', 'Applying to the bot'] as const;

/** Index into IMPORT_STEPS; 3 = past the last one (READY). */
export function importJobStepIndex(status: ImportJobStatus): number {
    switch (status) {
        case 'PENDING':
        case 'PREPARING':
        case 'PREPARED':
            return 0;
        case 'COMMITTING_DB':
            return 1;
        case 'DB_COMMITTED':
        case 'COMMITTING_ENGINE':
            return 2;
        case 'READY':
            return 3;
        default:
            return 0;
    }
}

export function importJobLabel(status: ImportJobStatus, error: string | null): string {
    switch (status) {
        case 'READY':
            return 'Done';
        case 'FAILED':
            return `Failed: ${error || 'unknown error'}`;
        case 'CANCELLING':
            return 'Cancelling';
        case 'CANCELLED':
            return 'Cancelled';
        default:
            return IMPORT_STEPS[importJobStepIndex(status)];
    }
}

export function isImportStuck(status: ImportJobStatus, lastChangeAt: number, now: number): boolean {
    return !isFinalImportStatus(status) && now - lastChangeAt > IMPORT_STUCK_AFTER_MS;
}

/**
 * Retry / Cancel only on an open job that reported an error or stopped moving. A final job takes
 * neither (the server answers 409): a FAILED import is submitted again.
 */
export function importJobActions(
    status: ImportJobStatus,
    error: string | null,
    stuck: boolean,
): { retry: boolean; cancel: boolean } {
    if (isFinalImportStatus(status) || !(stuck || error)) return { retry: false, cancel: false };
    return { retry: true, cancel: status !== 'CANCELLING' };
}
